import { ResumeAnalysis } from "lib/resume-scoring/resume-analysis/resume-analyse-types";

/**
 * Removes spelling mistakes from resume analysis where the correct and incorrect words are the same when ignoring case
 * @param analysis The original resume analysis
 * @returns Updated resume analysis with fake spelling mistakes removed
 */
export const removeFakeSpellingMistakes = (analysis: ResumeAnalysis): ResumeAnalysis => {
  // Handle null/undefined analysis
  if (!analysis) return analysis;
  
  // Create a deep copy of the analysis to avoid mutating the original
  const cleanedAnalysis = JSON.parse(JSON.stringify(analysis)) as ResumeAnalysis;
  
  // Helper function to filter out fake spelling mistakes
  const filterFakeSpellingMistakes = (spellingMistakes: Array<any>) => {
    if (!spellingMistakes) return [];
    
    return spellingMistakes.filter(mistake => {
      // Skip if mistake is undefined
      if (!mistake) return false;
      
      // If either correct or incorrect is null/undefined, keep the mistake
      if (!mistake.correct || !mistake.incorrect) return true;
      
      // Check if both values are strings before comparing
      if (typeof mistake.correct !== 'string' || typeof mistake.incorrect !== 'string') return true;
      
      // Remove mistakes where correct and incorrect are the same when ignoring case
      return mistake.correct.toLowerCase() !== mistake.incorrect.toLowerCase();
    });
  };
  
  // Clean profile summary analysis
  if (cleanedAnalysis.profile?.summaryAnalysis?.analysis?.spellingMistakes) {
    cleanedAnalysis.profile.summaryAnalysis.analysis.spellingMistakes = 
      filterFakeSpellingMistakes(cleanedAnalysis.profile.summaryAnalysis.analysis.spellingMistakes);
  }
  
  // Clean work experiences analysis
  if (cleanedAnalysis.workExperiences?.individualWorkExperiencesAnalysis) {
    for (const experience of cleanedAnalysis.workExperiences.individualWorkExperiencesAnalysis) {
      if (experience.descriptionAnalysis) {
        for (const desc of experience.descriptionAnalysis) {
          if (desc.analysis?.spellingMistakes) {
            desc.analysis.spellingMistakes = filterFakeSpellingMistakes(desc.analysis.spellingMistakes);
          }
        }
      }
    }
  }
  
  // Clean educations analysis
  if (cleanedAnalysis.educations?.individualEducationsAnalysis) {
    for (const edu of cleanedAnalysis.educations.individualEducationsAnalysis) {
      if (edu.descriptionAnalysis) {
        for (const desc of edu.descriptionAnalysis) {
          if (desc.analysis?.spellingMistakes) {
            desc.analysis.spellingMistakes = filterFakeSpellingMistakes(desc.analysis.spellingMistakes);
          }
        }
      }
    }
  }
  
  // Clean projects analysis
  if (cleanedAnalysis.projects?.individualProjectsAnalysis) {
    for (const project of cleanedAnalysis.projects.individualProjectsAnalysis) {
      if (project.descriptionAnalysis) {
        for (const desc of project.descriptionAnalysis) {
          if (desc.analysis?.spellingMistakes) {
            desc.analysis.spellingMistakes = filterFakeSpellingMistakes(desc.analysis.spellingMistakes);
          }
        }
      }
    }
  }
  
  // Clean custom sections if they exist
  if (cleanedAnalysis.custom?.descriptionAnalysis) {
    for (const desc of cleanedAnalysis.custom.descriptionAnalysis) {
      if (desc.analysis?.spellingMistakes) {
        desc.analysis.spellingMistakes = filterFakeSpellingMistakes(desc.analysis.spellingMistakes);
      }
    }
  }
  
  return cleanedAnalysis;
};